import React, { useState, useEffect } from 'react';
import {
  Box, Button, Input, FormControl, FormLabel,
  Heading, Text, Link, FormErrorMessage, Spinner
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { API_BASE_URL } from '../api/apiClient';

function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false); // new state

  // Redirect once login is successful
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleLogin = async () => {
    // Prevent multiple submissions
    if (isLoading) return;

    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      // navigation happens in useEffect when user updates
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.message ||
        'Login failed. Please try again.';
      setError(`${message} (Target: ${API_BASE_URL})`);
      setIsLoading(false); // re-enable button if login fails
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  // Determine which field to highlight
  const isEmailError = error?.toLowerCase().includes('email');
  const isPasswordError =
    error?.toLowerCase().includes('password') ||
    error?.toLowerCase().includes('credentials');

  return (
    <Box px={{ base: 4, md: 0 }} py={{ base: 6, md: 12 }}>
      <Box className="container">
        <Box maxW="md" mx="auto" mt={{ base: 6, md: 20 }} p={{ base: 6, md: 8 }} borderWidth={1} borderRadius="lg">
          <Heading mb={6}>Log In</Heading>

          <FormControl mb={4} isInvalid={isEmailError}>
            <FormLabel>Email</FormLabel>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              onKeyPress={handleKeyPress}
              isDisabled={isLoading} // disable input during login
            />
            {isEmailError && <FormErrorMessage>{error}</FormErrorMessage>}
          </FormControl>

          <FormControl mb={4} isInvalid={isPasswordError}>
            <FormLabel>Password</FormLabel>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              onKeyPress={handleKeyPress}
              isDisabled={isLoading}
            />
            {isPasswordError && <FormErrorMessage>{error}</FormErrorMessage>}
          </FormControl>

          <Button
            colorScheme="teal"
            onClick={handleLogin}
            width="full"
            isDisabled={isLoading}
            opacity={isLoading ? 0.7 : 1}
            cursor={isLoading ? 'not-allowed' : 'pointer'}
            leftIcon={isLoading ? <Spinner size="sm" /> : null}
          >
            {isLoading ? 'Logging in...' : 'Log In'}
          </Button>

          <Text mt={2} textAlign="right" fontSize="sm">
            <Link as={RouterLink} to="/forgot-password" color="teal.500">
              Forgot password?
            </Link>
          </Text>

          <Text mt={4}>
            Don’t have an account?{' '}
            <Link as={RouterLink} to="/signup" color="teal.500">
              Sign up here
            </Link>
          </Text>

          {error && !isEmailError && !isPasswordError && (
            <Text mt={4} color="red.500" textAlign="center">
              {error}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default Login;
